import {
  FilenetStatus,
  QuoteStatus,
  SpaSendBlockReason,
  SpaStatus,
  TradeAgreementStatus,
} from 'quotes/enums';
import { Color } from 'theme/enums';
import { IColorMap } from 'theme/types';

export const statusColorMap: IColorMap = {
  [QuoteStatus.DRAFT]: Color.WARNING,
  [QuoteStatus.EDITABLE_BY_CUSTOMER]: Color.NEUTRAL,
  [QuoteStatus.LOST]: Color.DANGER,
  [QuoteStatus.OPEN]: Color.PRIMARY,
  [QuoteStatus.WON]: Color.SUCCESS,
  [QuoteStatus.PUNCHED_OUT]: Color.SUCCESS,
  [QuoteStatus.ORDER_DRAFT_CUSTOMER]: Color.WARNING,
  [QuoteStatus.ORDER_DRAFT_VENDOR]: Color.WARNING,
  [QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER]: Color.PRIMARY,
  [QuoteStatus.ORDER_ORDERED]: Color.SUCCESS,
  [QuoteStatus.ORDER_ORDERING]: Color.WARNING,
  [QuoteStatus.ORDER_ORDER_FAILED]: Color.DANGER,
  // In limbo rather than failed, like Ordering — the label carries the distinction. Not
  // compiler-enforced: IColorMap is a loose index signature, a missing entry renders colourless.
  [QuoteStatus.ORDER_SUBMISSION_TIMED_OUT]: Color.WARNING,
  [QuoteStatus.ORDER_ACCEPTED]: Color.SUCCESS,
  [QuoteStatus.ORDER_CANCELED]: Color.DANGER,
  [QuoteStatus.ORDER_SHIPPED_PARTIAL]: Color.WARNING,
  [QuoteStatus.ORDER_SHIPPED_COMPLETE]: Color.SUCCESS,
};

// Order statuses in which the ordering system has, or may have, the order: no user edits.
// Ordering and Submission Timed Out are locked until a webhook resolves them; Order Failed is
// editable again.
export const LOCKED_ORDER_STATUSES: QuoteStatus[] = [
  QuoteStatus.ORDER_ORDERING,
  QuoteStatus.ORDER_SUBMISSION_TIMED_OUT,
  QuoteStatus.ORDER_ORDERED,
];

// Orders not yet handed to the ordering system.
// Mirrors BE NoDeliveryDateHelper::PRE_SUBMIT_STATUSES.
export const PRE_SUBMIT_ORDER_STATUSES: QuoteStatus[] = [
  QuoteStatus.ORDER_DRAFT_CUSTOMER,
  QuoteStatus.ORDER_DRAFT_VENDOR,
  QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER,
  QuoteStatus.ORDER_ORDER_FAILED,
];

// Pre-submit plus Submission Timed Out: the timed-out submit was rolled back, so no No Delivery
// Date day is stored until a webhook resolves the order. Kept apart from PRE_SUBMIT_ORDER_STATUSES
// because Submit refuses a timed-out order.
// Mirrors BE NoDeliveryDateHelper::UNFROZEN_STATUSES.
export const EARLIEST_UNFROZEN_ORDER_STATUSES: QuoteStatus[] = [
  ...PRE_SUBMIT_ORDER_STATUSES,
  QuoteStatus.ORDER_SUBMISSION_TIMED_OUT,
];

// Typed to the Color enum (not IColorMap) so TradeAgreementStatusCell can pass it straight to
// TextLine's `color` prop, which expects Color rather than the broader ColorPaletteProp.
export const tradeAgreementStatusColorMap: Record<TradeAgreementStatus, Color> = {
  [TradeAgreementStatus.PENDING]: Color.WARNING,
  [TradeAgreementStatus.SENT]: Color.SUCCESS,
  [TradeAgreementStatus.FAILED]: Color.DANGER,
  // Spec asks for orange; WARNING is the only amber/orange entry in the Joy palette we expose.
  [TradeAgreementStatus.UPDATED_POST_SENT]: Color.WARNING,
};

// Keyed on the enum (not IColorMap) so a new FileNet status can't silently render colourless.
export const filenetStatusColorMap: Record<FilenetStatus, Color> = {
  [FilenetStatus.FAILED]: Color.DANGER,
  [FilenetStatus.SENDING]: Color.PRIMARY,
  [FilenetStatus.SENT]: Color.SUCCESS,
};

// Translation keys per SpaSendBlockReason. Keyed on the enum so a new reason cannot silently
// render an empty tooltip or an unlabelled list row.
// Singular phrasing, for the single-send button's disabled tooltip.
export const spaSendBlockTooltipKeyMap: Record<SpaSendBlockReason, string> = {
  [SpaSendBlockReason.ALL_CUSTOMERS_ALL_SALES_LOCATIONS_NOT_PERMITTED]:
    'messageAllCustomersAllSalesLocationsNotAllowed',
  [SpaSendBlockReason.ALL_CUSTOMERS_NOT_PERMITTED]: 'messageAllCustomersSpaNotPermitted',
  [SpaSendBlockReason.INVALID_TRADE_AGREEMENT_DATES]: 'messageSendAsSpaBlockersInvalidDates',
  [SpaSendBlockReason.MISSING_REQUIRED_ATTRIBUTES]: 'messageSendAsSpaBlockersMissingAttributes',
  [SpaSendBlockReason.PROGRAM_NOT_PERMITTED]: 'messageSpaProgramNotPermitted',
};

// Short per-quote reason shown beside each id in the bulk dialogs' blocker lists.
export const spaSendBlockLabelKeyMap: Record<SpaSendBlockReason, string> = {
  [SpaSendBlockReason.ALL_CUSTOMERS_ALL_SALES_LOCATIONS_NOT_PERMITTED]:
    'labelVendorQuoteWithIdAllCustomersAllSalesLocations',
  [SpaSendBlockReason.ALL_CUSTOMERS_NOT_PERMITTED]: 'labelVendorQuoteWithIdAllCustomers',
  [SpaSendBlockReason.INVALID_TRADE_AGREEMENT_DATES]:
    'labelVendorQuoteWithIdInvalidTradeAgreementDates',
  [SpaSendBlockReason.MISSING_REQUIRED_ATTRIBUTES]:
    'labelVendorQuoteWithIdMissingRequiredAttributes',
  [SpaSendBlockReason.PROGRAM_NOT_PERMITTED]: 'labelVendorQuoteWithIdProgram',
};

export const spaStatusLabelKeyMap: Record<SpaStatus, string> = {
  [SpaStatus.LIVE]: 'labelSpaStatusLive',
  [SpaStatus.PLANNING]: 'labelSpaStatusPlanning',
};

export const DEFAULT_VALID_UNTIL_OFFSET = 30;

export const PURCHASE_ORDER_NUMBER_MAX_LENGTH = 50;
